import { useTranslation } from 'react-i18next';
import {
  useDeleteTranslation,
  useGetLanguages,
  useGetTranslations,
} from '../../../services/redux/api/translationsAPI';
import { useMemo, useState } from 'react';
import { useAccess } from '../../../hooks/useAccess';
import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import { ITranslation } from '../../../types/interfaces/translation';
import { getCellReducers } from './helpers/getCellReducers';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';
import { FormikProps, useFormik } from 'formik';

export function useTranslationsComponent(classes) {
  const { t } = useTranslation();
  const { data: translations } = useGetTranslations({ withLanguage: true });
  const [itemForDelete, setItemForDelete] = useState<ITranslation>(null);
  const [itemForEditing, setItemForEditing] = useState<ITranslation>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const { access } = useAccess();

  const [deleteTranslation] = useDeleteTranslation();
  const { data: languages } = useGetLanguages();
  const { order: defaultOrder, orderBy } = getDefaultSettings(t);

  const filterFormik: FormikProps<any> = useFormik({
    enableReinitialize: true,
    initialValues: {
      key: { id: 'all', name: t('common.all') },
      language: { id: 'all', name: t('common.all') },
      emptyValue: { id: 'all', name: t('common.all') },
    },
    onSubmit: () => {},
  });

  const filteredValues = useMemo(
    () =>
      translations?.filter((translation: ITranslation) => {
        const keyFilterId = filterFormik.values.key?.id ?? 'all';
        const languageFilterId = filterFormik.values.language?.id ?? 'all';
        const emptyValueFilterId = filterFormik.values.emptyValue?.id ?? 'all';
        const filterValue = filter.toLowerCase();

        if (
          !String(translation.key ?? '')
            .toLowerCase()
            .includes(filterValue) &&
          !String(translation.language?.name ?? '')
            .toLowerCase()
            .includes(filterValue) &&
          !String(translation.value ?? '')
            .toLowerCase()
            .includes(filterValue)
        ) {
          return false;
        }

        if (keyFilterId !== 'all' && translation.key !== keyFilterId)
          return false;

        if (
          languageFilterId !== 'all' &&
          translation.languageId !== languageFilterId
        )
          return false;

        if (emptyValueFilterId !== 'all') {
          if (emptyValueFilterId === true && !translation.value) {
            return true;
          } else if (emptyValueFilterId === false && translation.value) {
            return true;
          }
          return false;
        }

        return true;
      }),
    [translations, filter, filterFormik.values],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    languages,
    {
      canEdit: access?.translations?.canEdit ?? false,
      canDelete: access?.translations?.canDelete ?? false,
    },
    (translation: ITranslation) => {
      setItemForEditing(translation);
      setShowEditModal(true);
    },
    (property: ITranslation) => {
      setItemForDelete(property);
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.EXERCISE_PROPERTY_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const { order, columnsList, sortedArray, onSortColumns, onSort } =
    useTableData;

  function onItemDelete() {
    setItemForDelete(null);
    deleteTranslation({ id: itemForDelete.id });
  }

  const filterKeyOptions = [
    {
      id: 'all',
      name: t('common.all'),
    },
    ...Array.from(new Set(translations?.map((t) => t.key) || [])).map(
      (key) => ({
        id: key,
        name: key,
      }),
    ),
  ];

  const filterLanguageOptions = [
    {
      id: 'all',
      name: t('common.all'),
    },
    ...Array.from(new Set(translations?.map((t) => t.languageId) || [])).map(
      (languageId) => {
        const language = languages?.find((l) => l.id === languageId);
        return {
          id: languageId,
          name: language?.name || language?.key,
        };
      },
    ),
  ];

  const filterIsEmptyTranslationOptions = [
    {
      id: 'all',
      name: t('common.all'),
    },
    {
      id: true,
      name: t('common.empty'),
    },
    {
      id: false,
      name: t('common.notEmpty'),
    },
  ];

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filter,
    setFilter,
    showEditModal,
    setShowEditModal,
    itemForEditing,
    setItemForEditing,
    filterKeyOptions,
    filterLanguageOptions,
    filterIsEmptyTranslationOptions,
    filterFormik,
  };
}
